import { useNavigate } from "react-router-dom";
import { Phone } from "lucide-react";
import { ConfirmDialog } from "@/pages/settings/email/confirm-dialog";
import { useCallerIdStore } from "@/store/phone-numbers/caller-id.store";
import { useTwilioState } from "@/context/twilio.context";
import useAuthUser from "@/hooks/useAuthUser";
import { PATH_SETTINGS_PHONE_NUMBERS } from "@/routes/paths";

/**
 * "Set up a phone number first" prompt — the caller-ID twin of
 * EmailConnectPrompt (lead-email.tsx). Mounted once in DashboardLayout and
 * opened by requireOutboundNumber() whenever an agent tries to call or text a
 * lead with no number to go out from (neither their own nor the company line).
 *
 * Admins (company:edit, the Phone Numbers settings permission) get a CTA to
 * that settings page — in a new tab during a live call, where leaving the page
 * is blocked. Agents can't fix it themselves, so they just get the ask-an-admin
 * explanation.
 */
const OutboundNumberPrompt = () => {
  const open = useCallerIdStore((s) => s.promptOpen);
  const closePrompt = useCallerIdStore((s) => s.closePrompt);
  const { hasPermission } = useAuthUser();
  const { isCallActive } = useTwilioState();
  const navigate = useNavigate();
  const canManage = hasPermission("company:edit");

  const goToSettings = () => {
    if (isCallActive) {
      window.open(PATH_SETTINGS_PHONE_NUMBERS, "_blank", "noopener,noreferrer");
    } else {
      navigate(PATH_SETTINGS_PHONE_NUMBERS);
    }
  };

  return (
    <ConfirmDialog
      open={open}
      onClose={closePrompt}
      onConfirm={canManage ? goToSettings : closePrompt}
      tone="brand"
      icon={Phone}
      title="Set up a phone number first"
      description={
        canManage ? (
          <>
            Calls and texts leave Iceberger from your assigned number or the company line, and
            neither is set up yet. Get a number from the Phone Numbers settings — it only takes a
            minute.
            {isCallActive && <> The settings open in a new tab, so your call keeps going.</>}
          </>
        ) : (
          <>
            Calls and texts leave Iceberger from your assigned number or the company line, and you
            don't have either yet. Ask an admin to assign you a number, then try again.
          </>
        )
      }
      confirmLabel={canManage ? "Go to phone numbers" : "Got it"}
      cancelLabel="Not now"
    />
  );
};

export default OutboundNumberPrompt;
